import type { CatalogueItem, Catalogue, Shelf } from './types';

/** The chip that means "no filter". */
export const CATEGORY_ALL = '__all__';

/**
 * The shop's shelves, however the payload expressed them.
 *
 * An agent that folds `category.upserted` sends `categoryList` with stable ids
 * and the shop's order; a server that does not, or a build that never folded
 * the type, sends only the flat names. Both normalise to the same shape, and
 * where there is no id the name IS the id — which is exactly what the screen
 * did before ids existed.
 *
 * Only shelves something sits on. `categoryList` deliberately carries empty
 * ones, because a shop's arrangement cannot be derived from its stock, but at a
 * counter a chip that filters to nothing is a dead tap.
 */
export function shelvesFrom(data: Pick<Catalogue, 'categories' | 'categoryList'>): Shelf[] {
  const names = new Set(data.categories);
  const list = (data.categoryList ?? []).filter((c) => names.has(c.name));
  if (list.length > 0) return list.map((c) => ({ id: c.id, name: c.name }));
  return data.categories.map((name) => ({ id: name, name }));
}

/**
 * Whether an item belongs on the selected shelf.
 *
 * Identity first, name as a fallback. Keying on the name alone meant a category
 * renamed in the panel silently matched nothing — the cashier's chip kept its
 * old label, the grid emptied, and only tapping another chip recovered it. The
 * name is still checked because an item whose category row has not replicated
 * carries an empty categoryId, and dropping it from its own shelf would be a
 * worse bug than the one this fixes.
 */
export function onShelf(item: CatalogueItem, shelfId: string, shelves: Shelf[]): boolean {
  if (shelfId === CATEGORY_ALL) return true;
  if (item.categoryId && item.categoryId === shelfId) return true;
  const shelf = shelves.find((c) => c.id === shelfId);
  return shelf !== undefined && item.category === shelf.name;
}
